import React, { useContext, useEffect, useState, useRef } from "react";
import FormDataContext from "../../../Contexts/FormData.context";
import Template from "../../Template/Template";
import parser from "html-react-parser";
import { useParams } from "react-router-dom";
import TemplateDataContext from "../../../Contexts/TemplateData.context";
import { jsonKeyMapping } from "../../../Config/Constants";
import { isStepCompleted } from "../../../Util/StepCompletionHelper";
import MissingInfoModal from "./MissingInfoModal";
import "./ReviewSummary.css";

type Props = { templatePrev?: React.RefObject<HTMLDivElement> };

const ReviewSummary = ({ templatePrev }: Props) => {
  const { state } = useContext(FormDataContext);
  const { formData } = state;
  const { template } = useParams();
  const { state: templateState } = useContext(TemplateDataContext);
  const [htmlContent, setHtmlContent] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const contentPaneRef = useRef<HTMLDivElement | null>(null);
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const [computedHeight, setComputedHeight] = useState<number | undefined>(undefined);

  const activeSteps = templateState?.activeTemplateSteps || [];
  const stepsWithFields = React.useMemo(() => {
    return (activeSteps || []).filter(
      (s: any) =>
        s &&
        s[jsonKeyMapping.FORM_GROUP] &&
        Array.isArray(s[jsonKeyMapping.FORM_GROUP]) &&
        s[jsonKeyMapping.FORM_GROUP].length > 0
    );
  }, [activeSteps]);
  const allStepsCompleted = React.useMemo(() => {
    if (!stepsWithFields || stepsWithFields.length === 0) return false;
    return stepsWithFields.every((s: any) => isStepCompleted(s, formData));
  }, [stepsWithFields, formData]);

  const formDataSignature = React.useMemo(() => {
    if (!formData) return "__empty__";
    try {
      return Object.keys(formData)
        .filter((k) => !k.startsWith("meta-data-"))
        .sort()
        .map((k) => `${k}:${String((formData as any)[k])}`)
        .join("|~|");
    } catch {
      return String(Date.now());
    }
  }, [formData]);

  useEffect(() => {
    if (!template) return;
    let observer: MutationObserver | null = null;
    const handles: number[] = [];
    let lastHtml: string | null = null;
    const read = () => {
      const el = document.getElementById("getHtml");
      if (!el) return;
      const raw = el.innerHTML;
      if (raw && raw.trim() && raw !== lastHtml) {
        lastHtml = raw;
        setHtmlContent(raw);
      }
    };
    [30, 80, 160, 320, 650, 1200].forEach((ms) => handles.push(window.setTimeout(read, ms)));
    const attach = () => {
      const el = document.getElementById("getHtml");
      if (!el || observer) return;
      observer = new MutationObserver(read);
      observer.observe(el, { childList: true, subtree: true, characterData: true });
      read();
    };
    const wait = window.setInterval(() => {
      attach();
      if (observer) window.clearInterval(wait);
    }, 40);
    handles.push(wait as unknown as number);
    return () => {
      handles.forEach((h) => window.clearTimeout(h));
      if (observer) observer.disconnect();
    };
  }, [template, formDataSignature]);

  useEffect(() => {
    // Defer assignment to ensure it overwrites any earlier step description injection
    Promise.resolve().then(() => {
      if (allStepsCompleted) {
        document.body.dataset.tooltipDefaultTitle = "How to execute this document";
        document.body.dataset.tooltipDefaultDescription = `To make your lease legally binding:\n\n- All tenants and the landlord must sign. You can do this electronically or in person.\n- No notarization is typically required, but local rules may vary — double-check if you’re in a rent-controlled area.\n- Both parties should review the final document for accuracy, especially payment terms and termination clauses.\n- If your state requires filing the lease (rare for residential leases), contact your local housing office.`;
      } else {
        document.body.dataset.tooltipDefaultTitle = "Complete the missing sections";
        document.body.dataset.tooltipDefaultDescription = `A few details are often left blank when drafting a lease:\n\n- Security deposit amount: This protects the landlord against damage or unpaid rent.\n- Lease start and end dates: These define when the tenancy legally begins and ends.\n- Utilities and maintenance responsibilities: Clarify who pays for electricity, water, trash, etc. This avoids confusion later.`;
      }
    });
    return () => {
      delete (document.body.dataset as any).tooltipDefaultTitle;
      delete (document.body.dataset as any).tooltipDefaultDescription;
    };
  }, [allStepsCompleted]);

  useEffect(() => {
    document.body.classList.add("review-mode");
    return () => document.body.classList.remove("review-mode");
  }, []);

  useEffect(() => {
    if (!htmlContent || !contentPaneRef.current) return;
    const root = contentPaneRef.current;
    const tokenRegex = /^\[[A-Z0-9_]{10,}\]$/;
    root.querySelectorAll("strong, em, span").forEach((el) => {
      if (el.childElementCount === 0) {
        const txt = el.textContent?.trim();
        if (txt && tokenRegex.test(txt)) {
          el.classList.add("wrap-token");
          if (!txt.includes("\u200b")) {
            el.textContent = txt.replace(/_/g, "_\u200b");
          }
        }
      }
    });
  }, [htmlContent]);

  // Responsive height logic with breakpoints:
  // 1. Determine viewport height (vh)
  // 2. Compute space from banner bottom to button wrapper top target, reserving:
  //    - 20px gap below banner
  //    - button height + 20px gap above button
  // 3. Apply tiered caps based on viewport height, never exceeding 750.
  useEffect(() => {
    const calcHeight = () => {
      if (!previewRef.current) return;
      const vh = window.innerHeight;
      const bannerBottom = bannerRef.current?.getBoundingClientRect().bottom ?? 0;
      const buttonWrapper = document.querySelector(
        '.form-navigation-bar-wrapper[data-complete-agreement-button]'
      ) as HTMLElement | null;
      const buttonHeight = buttonWrapper?.getBoundingClientRect().height ?? 56; // fallback guess
      const gaps = 20 + 20; // gap below banner + gap above button
      const reserved = gaps + buttonHeight;
      let available = vh - bannerBottom - reserved;
      // Breakpoint-based dynamic cap (smaller screens => lower cap)
      const tierCap = vh >= 1000 ? 750 : vh >= 880 ? 640 : vh >= 780 ? 560 : vh >= 700 ? 480 : 420;
      // Minimum readable height
      const minH = 200;
      available = Math.min(available, tierCap, 750);
      if (available < minH) available = minH;
      setComputedHeight(Math.floor(available));
    };
    calcHeight();
    window.addEventListener('resize', calcHeight);
    const ro = new ResizeObserver(calcHeight);
    if (bannerRef.current) ro.observe(bannerRef.current);
    const buttonWrapper = document.querySelector(
      '.form-navigation-bar-wrapper[data-complete-agreement-button]'
    ) as HTMLElement | null;
    if (buttonWrapper) ro.observe(buttonWrapper);
    return () => {
      window.removeEventListener('resize', calcHeight);
      ro.disconnect();
    };
  }, [htmlContent, allStepsCompleted]);

  return (
    <div className="review-summary-redesign">
      {modalOpen && (
        <MissingInfoModal open={modalOpen} onClose={() => setModalOpen(false)} />
      )}
      <div
        ref={bannerRef}
        className="review-status-banner redesign"
        style={{ cursor: !allStepsCompleted ? "pointer" : undefined, marginTop: 30 }}
        onClick={() => !allStepsCompleted && setModalOpen(true)}
      >
        <div className="status-icon">
          <img
            className="icon-img"
            src={
              allStepsCompleted
                ? `${process.env.PUBLIC_URL}/static/images/check-step.svg`
                : `${process.env.PUBLIC_URL}/static/images/text.svg`
            }
            alt={allStepsCompleted ? "Completed" : "Incomplete"}
            width={24}
            height={24}
          />
        </div>
        <div className="status-text-wrap">
          <div className={`review-status-text ${allStepsCompleted ? "complete" : ""}`}>
            {!allStepsCompleted ? (
              <>You have <span className="mbold">missing information</span> you may want to fill out.</>
            ) : (
              <>Your document <span className="mbold">is ready</span></>
            )}
          </div>
        </div>
      </div>
      <div
        className="review-preview"
        ref={previewRef}
        style={computedHeight ? { maxHeight: computedHeight, height: computedHeight, transition: 'height 150ms ease, max-height 150ms ease' } : undefined}
      >
        {template && (
          <div style={{ display: "none" }} aria-hidden>
            <Template templateKey={template} isInnerPage templatePrev={templatePrev} />
          </div>
        )}
        {htmlContent ? (
          <div
            className="review-template-content"
            ref={(el) => {
              contentPaneRef.current = el;
              if (templatePrev && typeof templatePrev === "object") {
                try {
                  (templatePrev as React.MutableRefObject<HTMLDivElement | null>).current = el;
                } catch {}
              }
            }}
          >
            {parser(htmlContent)}
          </div>
        ) : (
          <div className="review-preview-loading">
            {template ? "Loading preview..." : "Template not selected."}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReviewSummary;
